import { Link } from "react-router-dom";
import { ArrowRight, Music, BookOpen, Heart, Clock, MapPin, MessageCircle } from "lucide-react";
import WorldMap from "@/components/WorldMap";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-[#FAFAF7]" data-testid="hero-section">
        <div className="absolute inset-0 opacity-90">
          <WorldMap variant="light" />
        </div>
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: "radial-gradient(circle at 75% 35%, rgba(18,63,51,0.08) 0%, transparent 55%), radial-gradient(circle at 15% 85%, rgba(228,225,216,0.5) 0%, transparent 45%)"
        }} />

        <div className="relative w-full max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-32 pb-24">
          <div className="max-w-3xl">
            <h1 className="reveal reveal-delay-1 font-serif text-[#0B1D2E] text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight font-semibold">
              <span className="block">Changing Lives.</span>
              <span className="block italic text-[#123F33]">Making Disciples.</span>
              <span className="block">Reaching the World.</span>
            </h1>
            <p className="reveal reveal-delay-2 mt-10 text-lg sm:text-xl text-[#4A5562] leading-relaxed max-w-2xl">
              A local church in DeLand, Florida — faithfully preaching the life-changing message of Jesus Christ. Come join us.
            </p>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#4A5562]/60 text-xs uppercase tracking-[0.3em] flex flex-col items-center gap-2 animate-pulse">
          <span>Scroll</span>
          <div className="w-px h-8 bg-[#4A5562]/40" />
        </div>
      </section>

      {/* SERVICE TIMES */}
      <section className="bg-[#F3F2EB] text-[#0B1D2E] relative" data-testid="service-times-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-4">
              <div className="text-xs uppercase tracking-[0.28em] text-[#123F33] mb-4 font-semibold">When We Gather</div>
              <h2 className="font-serif text-4xl sm:text-5xl leading-tight text-[#0B1D2E]">
                Service <span className="italic text-[#123F33]">Times</span>
              </h2>
            </div>
            <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                { day: "Sunday", time: "11:00 AM", label: "Morning Worship" },
                { day: "Sunday", time: "7:00 PM", label: "Evening Service" },
                { day: "Wednesday", time: "7:00 PM", label: "Evening Service" },
              ].map((s, i) => (
                <div
                  key={i}
                  data-testid={`service-${s.day.toLowerCase()}-${i}`}
                  className="p-8 bg-white border border-black/5 rounded-2xl hover:-translate-y-1 hover:shadow-md transition-all"
                >
                  <div className="text-xs tracking-[0.22em] uppercase text-[#123F33] mb-3 font-semibold">{s.day}</div>
                  <div className="font-serif text-3xl text-[#0B1D2E]">{s.time}</div>
                  <div className="mt-2 text-sm text-[#4A5562]">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-12 flex items-center gap-2 text-sm text-[#4A5562]">
            <MapPin className="w-4 h-4 text-[#123F33]" />
            <span>The Potter's House · DeLand, Florida</span>
          </div>
        </div>
      </section>

      {/* WHAT TO EXPECT */}
      <section className="bg-white py-28 md:py-36" data-testid="what-to-expect-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-2xl mb-20">
            <div className="text-xs uppercase tracking-[0.28em] text-[#123F33] mb-4 font-semibold">What to Expect</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#0B1D2E] leading-tight">
              Simple. Spirit-filled. <span className="italic">About Jesus.</span>
            </h2>
            <p className="mt-6 text-lg text-[#4A5562] leading-relaxed">
              No performance. No pressure. Just honest worship, clear Bible teaching, and space to meet with God.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { Icon: Music, title: "Music Worship", desc: "Songs that lift the name of Jesus and turn our hearts toward Him." },
              { Icon: BookOpen, title: "Bible-Based Preaching", desc: "Practical, honest teaching rooted in Scripture and relevant to real life." },
              { Icon: Heart, title: "Altar Call", desc: "A moment to respond, pray, and receive prayer from someone who cares." },
              { Icon: Clock, title: "About 1 Hour", desc: "Come as you are. Leave changed. Services typically run about sixty minutes." },
            ].map(({ Icon, title, desc }, i) => (
              <div
                key={i}
                data-testid={`expect-card-${i}`}
                className="bg-[#F3F2EB] p-8 rounded-2xl border border-black/5 hover:-translate-y-1 hover:shadow-lg transition-all duration-500"
              >
                <div className="w-12 h-12 rounded-full bg-[#123F33]/10 flex items-center justify-center mb-6">
                  <Icon className="w-5 h-5 text-[#123F33]" />
                </div>
                <h3 className="font-serif text-xl text-[#0B1D2E] mb-3">{title}</h3>
                <p className="text-sm text-[#4A5562] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          {/* Speaking in tongues — welcoming note */}
          <div
            data-testid="tongues-note"
            className="mt-10 bg-[#123F33]/5 border border-[#123F33]/15 rounded-2xl p-8 md:p-10 flex flex-col md:flex-row gap-6 items-start"
          >
            <div className="w-11 h-11 rounded-full bg-[#123F33]/10 flex items-center justify-center shrink-0">
              <MessageCircle className="w-5 h-5 text-[#123F33]" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-[#123F33] font-semibold mb-2">
                A Note for First-Time Guests
              </div>
              <h3 className="font-serif text-2xl text-[#0B1D2E] leading-tight">
                You may hear someone speaking in tongues.
              </h3>
              <p className="mt-3 text-[#4A5562] leading-relaxed max-w-3xl">
                During worship or prayer, you may hear people speaking in tongues. This is a biblical
                expression of prayer and worship, and a normal part of our Pentecostal services. No
                pressure — just feel free to worship in the way that's most authentic to you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* INVITATION */}
      <section className="bg-[#FAFAF7] py-20" data-testid="invitation-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="bg-[#123F33] rounded-3xl px-8 md:px-16 py-20 md:py-28 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3" />
            <div className="relative max-w-3xl">
              <h2 className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.1]">
                Come as you are.
                <span className="block italic text-[#E4E1D8] mt-2">Leave with Jesus.</span>
              </h2>
              <p className="mt-8 text-lg text-white/80 leading-relaxed max-w-xl">
                Whether this is your first time near a church or your hundredth — you're welcome here. Bring your questions, your burdens, your hope. We'd love to meet you this Sunday.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
